import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStats } from '../../../../context/StatsContext';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import { formatCurrencyPKR } from '../../../../utils/format';
import Preloader from '../../../../components/Preloader/Preloader';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { AlertCircle, Clock } from 'lucide-react';
import './DashboardHome.css';

const DashboardHome = () => {
  const { user } = useAuth();
  const { stats, loadingStats } = useStats();
  const navigate = useNavigate();
  const [adStatus, setAdStatus] = useState(null);

  useEffect(() => {
    const fetchAdStatus = async () => {
      try {
        const res = await api.get('/ad/status');
        setAdStatus(res.data);
      } catch (err) {
        if (err.response?.data) {
          setAdStatus(err.response.data);
        }
      }
    };
    fetchAdStatus();
  }, []);

  if (loadingStats && !stats) return <Preloader message="Loading Command Center..." />;
  if (!stats) return <div className="dbh-dash"><div className="dbh-dash__error">Failed to load dashboard data.</div></div>;

  // Values from Backend (Ground Truth)
  const leftCount = stats.team?.leftCount || 0;
  const rightCount = stats.team?.rightCount || 0;
  const pairBonus = stats.binaryIncome || 0;

  // Chart Data
  const treeData = [
    { name: 'Left Leg', value: leftCount, color: 'var(--awp-info)' },
    { name: 'Right Leg', value: rightCount, color: 'var(--awp-warning)' }
  ];
  
  // Custom PieChart label
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent === 0) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const adWatchIncome = stats.adWatchIncome || 0;

  const financeData = [
    { name: 'Withdrawn', amount: stats.withdrawals.approved || 0, fill: 'var(--awp-success)' },
    { name: 'Pending', amount: stats.withdrawals.pending || 0, fill: 'var(--awp-warning)' },
    { name: 'Binary Income', amount: pairBonus || 0, fill: 'var(--awp-accent)' },
    { name: 'Ad Watch', amount: adWatchIncome, fill: 'var(--awp-info)' },
  ];

  return (
    <div className="dbh-dash">
      <div className="dbh-dash__header">
        <div className="dbh-dash__welcome">
          <h1 className="dbh-dash__title">Welcome, {user?.username || 'Partner'}!</h1>
        </div>
        <div className="dbh-dash__header-actions">
          <div className="dbh-dash__live-badge">
            <span className="dbh-dash__live-dot" style={{fontWeight: 'bold'}}></span>
            Alpha Ads Watch
          </div>
        </div>
      </div>

      {/* Ad Status Alert Banner */}
      {adStatus && (!adStatus.eligible || adStatus.warning) && (
        <div className={`dbh-ad-alert ${!adStatus.eligible ? 'dbh-ad-alert--error' : 'dbh-ad-alert--warning'}`}>
          <div className="dbh-ad-alert__icon">
            {!adStatus.eligible ? <AlertCircle size={24} /> : <Clock size={24} />}
          </div>
          <div className="dbh-ad-alert__body">
            <h4 className="dbh-ad-alert__label">{!adStatus.eligible ? 'Action Required' : 'Upcoming Deadline'}</h4>
            <p className="dbh-ad-alert__msg">{adStatus.message || adStatus.warning}</p>
          </div>
          {!adStatus.eligible && (
            <button 
              className="dbh-ad-alert__btn"
              onClick={() => navigate(
                ['no_pair_after_week_1', 'no_pair_last_week', 'unbalanced_team'].includes(adStatus.reason)
                  ? '/dashboard/create-account'
                  : '/dashboard/watch-and-earn'
              )}
            >
              Learn More →
            </button>
          )}
        </div>
      )}

      {/* Expiring Soon Alert */}
      {stats.pins.expiringSoon && stats.pins.expiringSoon.length > 0 && (
        <div className="dbh-expiry-alert">
          <div className="dbh-expiry-alert__icon">⏰</div>
          <div className="dbh-expiry-alert__body">
            <strong>Urgent: {stats.pins.expiringSoon.length} E-Pin{stats.pins.expiringSoon.length > 1 ? 's are' : ' is'} expiring within 24 hours!</strong>
            <div className="dbh-expiry-alert__pins">
              {stats.pins.expiringSoon.map(p => (
                <span key={p.code} className="dbh-expiry-alert__pin">
                  <code>{p.code}</code> — expires {p.expiresAt}
                </span>
              ))}
            </div>
          </div>
          <button
            className="dbh-expiry-alert__cta"
            onClick={() => navigate('/dashboard/create-account')}
          >
            Use Now →
          </button>
        </div>
      )}

      {/* KPI Row (Top) */}
      <div className="dbh-dash__kpi-row">
        {/* Main Balance KPI */}
        <div className="dbh-kpi dbh-kpi--teal dbh-kpi--featured">
          <div className="dbh-kpi__icon featured">💰</div>
          <div className="dbh-kpi__data">
            <span className="dbh-kpi__value">Rs. {formatCurrencyPKR(stats.availableBalance)}</span>
            <span className="dbh-kpi__label">Available Balance</span>
          </div>
        </div>

        <div className="dbh-kpi dbh-kpi--purple" onClick={() => navigate('/dashboard/rewards')} style={{cursor: 'pointer'}}>
          <div className="dbh-kpi__icon">🤝</div>
          <div className="dbh-kpi__data">
            <span className="dbh-kpi__value">Rs. {formatCurrencyPKR(pairBonus)}</span>
            <span className="dbh-kpi__label">Binary Pair Income</span>
          </div>
        </div>

        <div className="dbh-kpi dbh-kpi--blue" onClick={() => navigate('/dashboard/rewards')} style={{cursor: 'pointer'}}>
          <div className="dbh-kpi__icon">🎁</div>
          <div className="dbh-kpi__data">
            <span className="dbh-kpi__value">Rs. {formatCurrencyPKR(stats.alphaBonusIncome)}</span>
            <span className="dbh-kpi__label">Alpha Bonus Total Income</span>
          </div>
          <div className="dbh-kpi__badge">rewards</div>
        </div>


        <div className="dbh-kpi dbh-kpi--amber" onClick={() => navigate('/dashboard/watch-and-earn')} style={{cursor: 'pointer'}}>
          <div className="dbh-kpi__icon">📺</div>
          <div className="dbh-kpi__data">
            <span className="dbh-kpi__value">Rs. {formatCurrencyPKR(adWatchIncome)}</span>
            <span className="dbh-kpi__label">Ads Watch</span>
          </div>
          <div className="dbh-kpi__badge">Ads Watch & Earn</div>
        </div>
      </div>


      {/* Analytics Charts Row */}
      <div className="dbh-dash__charts-row">
        {/* Tree Distribution PieChart */}
        <div className="dbh-chart-card">
          <div className="dbh-chart-header">
            <h3>⚖️ Binary Tree Balance</h3>
            <span>Left Leg vs Right Leg Distribution</span>
          </div>
          <div className="dbh-chart-body">
            {leftCount === 0 && rightCount === 0 ? (
              <div className="dbh-empty-state">
                No downline members yet.<br />Share your referral link to build your tree!
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={treeData}
                    cx="50%" cy="50%" labelLine={false} label={renderCustomizedLabel}
                    outerRadius={100} dataKey="value" stroke="none"
                  >
                    {treeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip cursor={{fill: 'var(--awp-surface-hover)'}} contentStyle={{ background: 'var(--awp-surface)', borderRadius: '8px', border: '1px solid var(--awp-border)', boxShadow: 'var(--awp-shadow-md)' }} itemStyle={{ color: 'var(--awp-text)' }} />
                  <Legend iconType="circle" verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Financial Breakdown BarChart */}
        <div className="dbh-chart-card">
          <div className="dbh-chart-header">
            <h3>📈 Account Financials</h3>
            <span>Income & Withdrawals History (PKR)</span>
          </div>
          <div className="dbh-chart-body">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financeData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--awp-divider)" />
                <XAxis dataKey="name" tick={{fontSize: 13, fill: 'var(--awp-text-muted)'}} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(val) => `Rs. ${val.toLocaleString()}`} tick={{fontSize: 12, fill: 'var(--awp-text-subtle)'}} tickLine={false} axisLine={false} />

                <RechartsTooltip cursor={{fill: 'var(--awp-surface-hover)'}} contentStyle={{ background: 'var(--awp-surface)', borderRadius: '8px', border: '1px solid var(--awp-border)', boxShadow: 'var(--awp-shadow-md)' }} itemStyle={{ color: 'var(--awp-text)' }} formatter={(val) => `Rs. ${val.toLocaleString()}`}/>
                <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                  {financeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Data Panels Row */}
      <div className="dbh-dash__panels">
        
        {/* Withdrawals Overvew */}
        <div className="dbh-panel">
          <div className="dbh-panel__head">
            <h3>💸 Withdrawals Pipeline</h3>
            <button className="dbh-panel__link" onClick={() => navigate('/dashboard/withdraw')}>View All →</button>
          </div>
          <div className="dbh-panel__metrics">
            <div className="dbh-metric">
              <div className="dbh-metric__bar dbh-metric__bar--green" />
              <div className="dbh-metric__info">
                <span className="dbh-metric__label">Total Withdrawn</span>
                <span className="dbh-metric__val">Rs. {formatCurrencyPKR(stats.withdrawals.approved)}</span>
              </div>
            </div>
            <div className="dbh-metric">
              <div className="dbh-metric__bar dbh-metric__bar--amber" />
              <div className="dbh-metric__info">
                <span className="dbh-metric__label">Pending Processing</span>
                <span className="dbh-metric__val">Rs. {formatCurrencyPKR(stats.withdrawals.pending)}</span>
              </div>
            </div>
            <div className="dbh-metric">
              <div className="dbh-metric__bar dbh-metric__bar--red" />
              <div className="dbh-metric__info">
                <span className="dbh-metric__label">Rejected</span>
                <span className="dbh-metric__val">{stats.withdrawals.rejectedCount} Requests</span>
              </div>
            </div>
          </div>
        </div>

        {/* E-Pin Ownership Panel */}
        <div className="dbh-panel">
          <div className="dbh-panel__head">
            <h3>🔑 My E-Pins</h3>
            <button className="dbh-panel__link" onClick={() => navigate('/dashboard/buy-pin')}>Buy Pins →</button>
          </div>
          <div className="dbh-dash__pin-row">
            <div className="dbh-dash__pin-card dbh-dash__pin-card--avail" onClick={() => navigate('/dashboard/view-pin')}>
              <span className="dbh-dash__pin-num">{stats.pins.available}</span>
              <span className="dbh-dash__pin-label">Ready to Use</span>
            </div>
            <div className="dbh-dash__pin-card dbh-dash__pin-card--used">
              <span className="dbh-dash__pin-num">{stats.pins.used}</span>
              <span className="dbh-dash__pin-label">Activated</span>
            </div>
            {stats.pins.expired > 0 && (
              <div className="dbh-dash__pin-card dbh-dash__pin-card--expired" onClick={() => navigate('/dashboard/view-pin')}>
                <span className="dbh-dash__pin-num">{stats.pins.expired}</span>
                <span className="dbh-dash__pin-label">Expired</span>
              </div>
            )}
          </div>
        </div>

        {/* Quick Jumps Panel */}
        <div className="dbh-panel">
          <div className="dbh-panel__head">
            <h3>⚡ Quick Actions</h3>
          </div>
          <div className="dbh-dash__actions">
            <button className="dbh-action" onClick={() => navigate('/dashboard/create-account')}>
              <div className="dbh-action__icon-box purple">🤝</div>
              <div className="dbh-action__text">
                <span className="dbh-action__title">Create Downline Account</span>
                <span className="dbh-action__sub">Requires 1 E-Pin code</span>
              </div>
            </button>
            <button className="dbh-action" onClick={() => navigate('/dashboard/rewards')}>
              <div className="dbh-action__icon-box green">🎁</div>
              <div className="dbh-action__text">
                <span className="dbh-action__title">Alpha Bonuses</span>
                <span className="dbh-action__sub">Claim your rank & team rewards</span>
              </div>
            </button>
            <button className="dbh-action" onClick={() => navigate('/dashboard/tree')}>
              <div className="dbh-action__icon-box amber">🌳</div>
              <div className="dbh-action__text">
                <span className="dbh-action__title">View My Tree</span>
                <span className="dbh-action__sub">Analyze your network structure</span>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardHome;